/**
 * Compositions (`settings.compositions`) — the source wall rendered by
 * `/composition/:id`.
 *
 * Everything here is pure: it is called both by the page and by the settings
 * validation, which runs outside React (and outside the SDK).
 */

import type { MeetingSourceExtended } from "../hooks/meeting";
import type {
  Composition,
  CompositionLayoutType,
  CompositionSlot,
  CompositionSlotFit,
  CompositionSlotRect,
} from "../interfaces/composition";

/** Empty cell marker, as in CSS `grid-template-areas`. */
export const EMPTY_AREA = ".";

/** CSS custom-ident, which is what a grid area name has to be. */
const AREA_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_-]*$/;

export interface CompositionTemplate {
  /** `rows` × `columns` grid of area names, `.` for the empty cells. */
  areas: string[][];
  columns: number;
  rows: number;
  /** Area names in reading order, without duplicates — the auto-assignment order. */
  names: string[];
  /** Ready for CSS `grid-template-areas`. */
  css: string;
}

export interface CompositionTemplateResult {
  template?: CompositionTemplate;
  error?: string;
}

/**
 * Parses a `grid-template` template into its grid of areas.
 *
 * Rows come either as array entries or as the lines of a single string. Short
 * rows are padded with `.` so `["a a a", "b"]` means what it looks like it
 * means, instead of producing a template CSS silently ignores.
 */
export const parseCompositionTemplate = (
  template: string | string[] | undefined,
): CompositionTemplateResult => {
  const lines = (Array.isArray(template) ? template : (template ?? "").split(/\r?\n/))
    .map((line) => String(line ?? "").trim())
    .filter((line) => line.length > 0);
  if (!lines.length) return { error: "template vide" };

  const rows = lines.map((line) => line.split(/\s+/));
  const columns = rows.reduce((max, row) => Math.max(max, row.length), 0);

  const invalid = rows.flat().find((name) => name !== EMPTY_AREA && !AREA_NAME_PATTERN.test(name));
  if (invalid) {
    return { error: `nom de zone invalide "${invalid}" (lettres, chiffres, "_" et "-", ne commence pas par un chiffre)` };
  }

  // Padding rather than refusing: a trailing row that only fills its first
  // cells is the natural way to write it, and CSS would reject the whole
  // template over the missing cells.
  const areas = rows.map((row) => [...row, ...Array(columns - row.length).fill(EMPTY_AREA)]);

  const names: string[] = [];
  areas.forEach((row) => row.forEach((name) => {
    if (name !== EMPTY_AREA && !names.includes(name)) names.push(name);
  }));

  // CSS only accepts rectangular areas, and drops the entire template when one
  // is not — which would blank the page instead of misplacing one slot.
  for (const name of names) {
    let minRow = Infinity; let maxRow = -1; let minCol = Infinity; let maxCol = -1;
    areas.forEach((row, r) => row.forEach((cell, c) => {
      if (cell !== name) return;
      minRow = Math.min(minRow, r); maxRow = Math.max(maxRow, r);
      minCol = Math.min(minCol, c); maxCol = Math.max(maxCol, c);
    }));
    for (let r = minRow; r <= maxRow; r += 1) {
      for (let c = minCol; c <= maxCol; c += 1) {
        if (areas[r][c] !== name) {
          return { error: `la zone "${name}" n'est pas rectangulaire` };
        }
      }
    }
  }

  return {
    template: {
      areas,
      columns,
      rows: areas.length,
      names,
      css: areas.map((row) => `"${row.join(" ")}"`).join(" "),
    },
  };
};

// ---------------------------------------------------------------------------
// Validation (settings save / remote load)
// ---------------------------------------------------------------------------

export interface CompositionSettingsError {
  path: string;
  message: string;
}

const layoutType = (composition: Composition): CompositionLayoutType => (
  composition.layout?.type === "free" ? "free" : "grid-template"
);

const isValidRect = (rect: CompositionSlotRect | undefined): boolean => (
  !!rect
  && [rect.x, rect.y, rect.width, rect.height].every((v) => typeof v === "number" && Number.isFinite(v))
  && rect.width > 0 && rect.height > 0
);

const slotSourceName = (slot: CompositionSlot): string | undefined => (
  typeof slot.source === "string" ? slot.source : slot.source?.name
);

/**
 * The checks the form schema cannot do: a template CSS would reject as a whole,
 * areas pointing at nothing, and slots with no placement. A composition that
 * fails here would render a blank page, so it is refused at save time.
 */
export const validateCompositionSettings = (settings: any): CompositionSettingsError[] => {
  const compositions = settings?.compositions;
  if (!Array.isArray(compositions)) return [];
  const errors: CompositionSettingsError[] = [];

  const seenIds = new Set<string>();
  compositions.forEach((composition: Composition, index: number) => {
    const path = `compositions[${index}]`;
    if (composition?.id) {
      if (seenIds.has(composition.id)) errors.push({ path: `${path}.id`, message: `identifiant "${composition.id}" déjà utilisé` });
      seenIds.add(composition.id);
    }

    const slots = Array.isArray(composition?.slots) ? composition.slots : [];
    slots.forEach((slot, slotIndex) => {
      if (!slotSourceName(slot)) {
        errors.push({ path: `${path}.slots[${slotIndex}].source`, message: "source manquante (nom ou définition inline)" });
      }
    });

    if (layoutType(composition) === "free") {
      slots.forEach((slot, slotIndex) => {
        if (!isValidRect(slot?.rect)) {
          errors.push({
            path: `${path}.slots[${slotIndex}].rect`,
            message: "position requise en disposition libre ({ x, y, width, height } en %, largeur et hauteur > 0)",
          });
        }
      });
      return;
    }

    const { template, error } = parseCompositionTemplate(composition?.layout?.template);
    if (error || !template) {
      errors.push({ path: `${path}.layout.template`, message: error ?? "template invalide" });
      return;
    }

    const usedAreas = new Set<string>();
    slots.forEach((slot, slotIndex) => {
      if (!slot?.area) return;
      if (!template.names.includes(slot.area)) {
        errors.push({ path: `${path}.slots[${slotIndex}].area`, message: `zone "${slot.area}" absente du template` });
        return;
      }
      if (usedAreas.has(slot.area)) {
        errors.push({ path: `${path}.slots[${slotIndex}].area`, message: `zone "${slot.area}" déjà occupée par un autre slot` });
      }
      usedAreas.add(slot.area);
    });

    const free = template.names.filter((name) => !usedAreas.has(name)).length;
    const unplaced = slots.filter((slot) => !slot?.area).length;
    if (unplaced > free) {
      errors.push({
        path: `${path}.slots`,
        message: `${unplaced} slot(s) sans zone pour ${free} zone(s) libre(s) dans le template`,
      });
    }
  });

  return errors;
};

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

export interface ResolvedCompositionSlot {
  /** Stable across renders — React key and stream lookup. */
  key: string;
  /** Key of the source feeding this slot in {@link ResolvedComposition.sources}. */
  sourceKey?: string;
  /** Set when the slot references a name no source declares. */
  missingSource?: string;
  label?: string;
  fit: CompositionSlotFit;
  muted: boolean;
  style: React.CSSProperties;
}

export interface ResolvedCompositionSource {
  key: string;
  source: MeetingSourceExtended;
}

export interface ResolvedComposition {
  id?: string;
  name?: string;
  background: string;
  mode: CompositionLayoutType;
  containerStyle: React.CSSProperties;
  slots: ResolvedCompositionSlot[];
  /** Distinct sources to open — several slots may share one. */
  sources: ResolvedCompositionSource[];
  /** Whatever made the composition unrenderable (shown on the page, not thrown). */
  errors: string[];
}

const DEFAULT_FIT: CompositionSlotFit = "contain";

/** `extra.playerFit` also carries `interactive`, which means nothing to a bare video. */
const sourceFit = (source: MeetingSourceExtended | undefined): CompositionSlotFit | undefined => {
  const fit = source?.extra?.playerFit;
  return fit && fit !== "interactive" ? (fit as CompositionSlotFit) : undefined;
};

/**
 * Builds the auto composition used when the settings declare none: every source
 * that is not explicitly hidden, in a square-ish grid.
 */
export const buildAutoComposition = (sources: MeetingSourceExtended[]): Composition => {
  const visible = sources.filter((source) => source.visible !== false);
  const columns = Math.max(1, Math.ceil(Math.sqrt(visible.length || 1)));
  const rows = Math.max(1, Math.ceil((visible.length || 1) / columns));
  const areaName = (index: number) => `s${index}`;
  const template = Array.from({ length: rows }, (_, r) => (
    Array.from({ length: columns }, (_, c) => {
      const index = r * columns + c;
      return index < visible.length ? areaName(index) : EMPTY_AREA;
    }).join(" ")
  ));
  return {
    id: undefined,
    name: "Toutes les sources",
    layout: { type: "grid-template", template, gap: 4 },
    slots: visible.map((source, index) => ({
      source: source.name ?? index.toString(),
      area: areaName(index),
      label: source.name,
    })),
  };
};

/**
 * Picks the composition the URL asks for. Without an id, the first one — a
 * single-composition config needs no id at all.
 */
export const findComposition = (
  compositions: Composition[] | undefined,
  id: string | undefined,
): Composition | undefined => {
  if (!compositions?.length) return undefined;
  if (!id) return compositions[0];
  return compositions.find((composition) => composition.id === id);
};

export interface ResolveCompositionOptions {
  /** Sources declared globally (`settings.sources`), the fallback lookup. */
  sources: MeetingSourceExtended[];
  /**
   * Normalizes a source declared by the composition itself (locally or inline)
   * — the caller owns it so it can keep object identities stable across renders
   * (the SDK rebuilds a source's media whenever the object changes).
   */
  normalizeSource: (source: Partial<MeetingSourceExtended>, key: string) => MeetingSourceExtended;
}

/**
 * Turns a composition into what the page renders: placed slots and the distinct
 * list of sources to open. Never throws — anything wrong lands in `errors` and
 * is shown on screen, because this page usually runs unattended on a display
 * nobody is sitting in front of.
 */
export const resolveComposition = (
  composition: Composition,
  options: ResolveCompositionOptions,
): ResolvedComposition => {
  const errors: string[] = [];
  const mode = layoutType(composition);
  const slots = (Array.isArray(composition.slots) ? composition.slots : []).filter(Boolean);

  // ---- Sources: composition-local first, then the global ones ----

  const localByName = new Map<string, Partial<MeetingSourceExtended>>();
  (composition.sources ?? []).forEach((source, index) => {
    const name = source?.name;
    if (!name) {
      errors.push(`sources[${index}] : source sans nom, ignorée`);
      return;
    }
    localByName.set(name, source);
  });
  const globalByName = new Map<string, MeetingSourceExtended>();
  options.sources.forEach((source) => {
    if (source.name && !globalByName.has(source.name)) globalByName.set(source.name, source);
  });

  const resolvedSources: ResolvedCompositionSource[] = [];
  const sourcesByKey = new Map<string, MeetingSourceExtended>();

  /**
   * Memoized per key: two slots pointing at the same source must share one
   * source object, so the page opens the device once and both play the same
   * stream.
   */
  const useSource = (key: string, build: () => MeetingSourceExtended): MeetingSourceExtended => {
    const existing = sourcesByKey.get(key);
    if (existing) return existing;
    const source = build();
    sourcesByKey.set(key, source);
    resolvedSources.push({ key, source });
    return source;
  };

  // ---- Placement ----

  const parsed = mode === "grid-template" ? parseCompositionTemplate(composition.layout?.template) : undefined;
  if (parsed?.error) errors.push(`layout.template : ${parsed.error}`);

  const takenAreas = new Set(slots.map((slot) => slot.area).filter(Boolean) as string[]);
  const freeAreas = (parsed?.template?.names ?? []).filter((name) => !takenAreas.has(name));

  const resolvedSlots: ResolvedCompositionSlot[] = slots.map((slot, index) => {
    const key = `slot-${index}`;

    // -- source --
    let sourceKey: string | undefined;
    let missingSource: string | undefined;
    let source: MeetingSourceExtended | undefined;
    if (typeof slot.source === "string") {
      const local = localByName.get(slot.source);
      const global = globalByName.get(slot.source);
      if (local) {
        sourceKey = `local:${slot.source}`;
        source = useSource(sourceKey, () => options.normalizeSource(local, sourceKey!));
      } else if (global) {
        sourceKey = `global:${slot.source}`;
        source = useSource(sourceKey, () => global);
      } else {
        missingSource = slot.source;
        errors.push(`slots[${index}] : source "${slot.source}" introuvable`);
      }
    } else if (slot.source) {
      const inline = slot.source;
      sourceKey = `inline:${inline.name ?? index}`;
      source = useSource(sourceKey, () => options.normalizeSource(inline, sourceKey!));
    } else {
      errors.push(`slots[${index}] : source manquante`);
    }

    // -- placement --
    const style: React.CSSProperties = {};
    if (mode === "free") {
      const rect = slot.rect;
      if (!isValidRect(rect)) {
        errors.push(`slots[${index}] : position manquante ou invalide`);
      } else {
        style.position = "absolute";
        style.left = `${rect!.x}%`;
        style.top = `${rect!.y}%`;
        style.width = `${rect!.width}%`;
        style.height = `${rect!.height}%`;
      }
      if (slot.zIndex !== undefined) style.zIndex = slot.zIndex;
    } else {
      const area = slot.area ?? freeAreas.shift();
      if (!area) errors.push(`slots[${index}] : aucune zone libre dans le template`);
      else if (parsed?.template && !parsed.template.names.includes(area)) {
        errors.push(`slots[${index}] : zone "${area}" absente du template`);
      } else if (area) style.gridArea = area;
    }

    return {
      key,
      sourceKey,
      missingSource,
      label: slot.label,
      fit: slot.fit ?? sourceFit(source) ?? DEFAULT_FIT,
      muted: slot.muted ?? false,
      style: { ...style, ...(slot.style ?? {}) },
    };
  });

  // ---- Container ----

  const containerStyle: React.CSSProperties = mode === "free"
    ? { position: "relative" }
    : {
        display: "grid",
        gridTemplateAreas: parsed?.template?.css,
        gridTemplateColumns: composition.layout?.columns ?? `repeat(${parsed?.template?.columns ?? 1}, 1fr)`,
        gridTemplateRows: composition.layout?.rows ?? `repeat(${parsed?.template?.rows ?? 1}, 1fr)`,
        gap: composition.layout?.gap ?? 0,
      };

  return {
    id: composition.id,
    name: composition.name,
    background: composition.background || "#000000",
    mode,
    containerStyle: { ...containerStyle, ...(composition.layout?.style ?? {}) },
    slots: resolvedSlots,
    sources: resolvedSources,
    errors,
  };
};
